import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import RestoreIcon from '@mui/icons-material/Restore';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import {
  settingsHistoryApi,
  settingsHistoryKey,
  type SettingsHistoryEntry,
  type SettingsHistorySubject,
} from '../../api/settings-history.api';
import { APP_LOCALE } from '../../utils/locale.js';

import { describeValue } from './settings-changes';

/**
 * The History panel of a settings section (DEV-133, DEV-137): who changed
 * what, when and why, newest first, refusals included. The panel itself
 * edits nothing; "Restore this version" (DEV-142) hands the entry to the
 * page, which restores it as a new change. `subject` names the section (and
 * `subjectId` the vendor, for a provider); `keys` narrows it to the fields
 * this section shows (Treasury shows only the commission-transfer fields of
 * the GuruPay connection).
 */

interface Props {
  readonly subject: SettingsHistorySubject;
  readonly subjectId?: string;
  readonly title: string;
  readonly keys?: readonly string[];
  /**
   * DEV-142: "Restore this version" on every entry the server names a restore
   * target for. Set only when the section offers restore and the person may
   * change the section; the panel closes and the page takes it from there.
   */
  readonly onRestore?: (entry: SettingsHistoryEntry) => void;
  /** The restore button's name ("Restore this version", "Propose restore"). */
  readonly restoreLabel?: string;
  /** A line under the panel's intro about restoring (four eyes, or why it is not offered). */
  readonly restoreNote?: string | undefined;
}

function useSettingsHistory(subject: SettingsHistorySubject, subjectId?: string, enabled = true) {
  return useQuery({
    queryKey: settingsHistoryKey(subject, subjectId),
    queryFn: () => settingsHistoryApi.list(subject, subjectId),
    enabled,
  });
}

/**
 * The header's "last changed" line from the History: the newest change that
 * went through. Undefined while it loads or when it cannot be read, so the
 * header simply leaves the line out.
 */
export function useSettingsLastChanged(
  subject: SettingsHistorySubject,
  subjectId?: string,
): { at: string | null; by: string | null } | undefined {
  const history = useSettingsHistory(subject, subjectId);
  if (history.data === undefined) return undefined;
  const newest = history.data.find((entry) => entry.outcome === 'SUCCEEDED');
  return { at: newest?.at ?? null, by: newest?.by ?? null };
}

function relevant(entry: SettingsHistoryEntry, keys: readonly string[] | undefined): SettingsHistoryEntry | null {
  if (keys === undefined) return entry;
  const changes = entry.changes.filter((c) => keys.includes(c.key));
  return changes.length === 0 ? null : { ...entry, changes, secretsChanged: [] };
}

interface EntryProps {
  readonly entry: SettingsHistoryEntry;
  readonly onRestore?: ((entry: SettingsHistoryEntry) => void) | undefined;
  readonly restoreLabel: string;
}

function Entry({ entry, onRestore, restoreLabel }: EntryProps): JSX.Element {
  const refused = entry.outcome !== 'SUCCEEDED';
  return (
    <Box component="li" sx={{ listStyle: 'none', py: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography variant="subtitle2">{new Date(entry.at).toLocaleString(APP_LOCALE)}</Typography>
        <Typography variant="body2" color="text.secondary">by {entry.by}</Typography>
        {refused ? <Chip size="small" color="error" variant="outlined" label="Refused" /> : null}
        {entry.restoredFrom !== undefined ? <Chip size="small" color="info" variant="outlined" label={`Restored ${entry.restoredFrom.length > 12 ? 'an earlier version' : `version ${entry.restoredFrom}`}`} /> : null}
        <Box sx={{ flex: 1 }} />
        {onRestore !== undefined && entry.restoreTarget !== undefined && !refused ? (
          <Button size="small" variant="outlined" startIcon={<RestoreIcon />} onClick={() => onRestore(entry)}
            aria-label={`${restoreLabel}: ${entry.summary}`}>
            {restoreLabel}
          </Button>
        ) : null}
      </Stack>
      {refused
        ? <Typography variant="body2" color="error.main">{entry.summary.replace(/^Refused: /, '')}</Typography>
        : entry.summary !== 'Settings changed' ? <Typography variant="body2">{entry.summary}</Typography> : null}
      <Typography variant="body2" color="text.secondary">Reason: {entry.reason.trim() === '' ? 'No reason given' : entry.reason}</Typography>
      <Box component="ul" sx={{ pl: 2, my: 0.5 }}>
        {entry.changes.map((change) => (
          <Typography component="li" variant="body2" key={change.key} sx={{ wordBreak: 'break-word' }}>
            {change.label}: {refused ? `tried ${describeValue(change.after)}` : `${describeValue(change.before)} → ${describeValue(change.after)}`}
          </Typography>
        ))}
        {entry.secretsChanged.map((name) => (
          <Typography component="li" variant="body2" key={name}>{name}: {refused ? 'change attempted' : 'changed'} (value never shown)</Typography>
        ))}
      </Box>
    </Box>
  );
}

function HistoryList({ subject, subjectId, keys, onRestore, restoreLabel = 'Restore this version' }: Omit<Props, 'title' | 'restoreNote'>): JSX.Element {
  const history = useSettingsHistory(subject, subjectId);
  if (history.isLoading) return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>;
  if (history.isError) return <Alert severity="error">Could not load the history.</Alert>;
  const entries = (history.data ?? []).map((e) => relevant(e, keys)).filter((e): e is SettingsHistoryEntry => e !== null);
  if (entries.length === 0) return <Typography variant="body2" color="text.secondary">No recorded changes yet.</Typography>;
  return (
    <Box component="ol" sx={{ p: 0, m: 0 }} aria-label="Recorded changes">
      {entries.map((entry, index) => (
        <Box key={entry.id}>
          {index > 0 ? <Divider /> : null}
          <Entry entry={entry} onRestore={onRestore} restoreLabel={restoreLabel} />
        </Box>
      ))}
    </Box>
  );
}

/** A History button that opens the panel. */
export function SettingsHistoryButton({ subject, subjectId, title, keys, onRestore, restoreLabel, restoreNote }: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  const restore = onRestore === undefined ? undefined : (entry: SettingsHistoryEntry): void => {
    setOpen(false);
    onRestore(entry);
  };
  return (
    <>
      <Button size="small" startIcon={<HistoryIcon />} onClick={() => setOpen(true)}>History</Button>
      <Drawer anchor="right" open={open} onClose={() => setOpen(false)} PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, p: 2 } }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ flex: 1 }} component="h2">History: {title}</Typography>
          <IconButton aria-label="Close history" onClick={() => setOpen(false)}><CloseIcon /></IconButton>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Every recorded change, newest first. Credentials are listed by name only.
          {restore !== undefined ? ' Restoring a version saves its values again as a new change, with a reason; nothing in the History is edited.' : ''}
        </Typography>
        {restoreNote !== undefined ? <Alert severity="info" sx={{ mb: 2 }}>{restoreNote}</Alert> : null}
        {open ? (
          <HistoryList
            subject={subject}
            {...(subjectId === undefined ? {} : { subjectId })}
            {...(keys === undefined ? {} : { keys })}
            {...(restore === undefined ? {} : { onRestore: restore })}
            {...(restoreLabel === undefined ? {} : { restoreLabel })}
          />
        ) : null}
      </Drawer>
    </>
  );
}
